import AlchemyModal, { ModalBody, ModalFooter, ModalHeader, ModalProps } from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { Checkbox } from 'UIKit/Checkbox'
import Text from 'UIKit/Text'
import { InhouseGameDetails, InhouseGameType } from 'config/types/game'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import React, { useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { Icons } from 'svgs'
import theme from 'theme'
import { ModalColorTheme } from 'theme/modal/colors'

const HotKeyModal: React.FC<
  ModalProps<{ hotKeyEnable: boolean; onHotKeyToggle: (enable: boolean) => void; game?: InhouseGameDetails }>
> = ({ onDismiss, data: { hotKeyEnable, onHotKeyToggle, game } }) => {
  const [checked, setChecked] = useState(hotKeyEnable)
  const refChecked = useRef(hotKeyEnable)

  useEffect(() => {
    return () => {
      onHotKeyToggle(refChecked.current)
    }
  }, [refChecked])

  return (
    <AlchemyModal
      onDismiss={onDismiss}
      maxWidth={['', '', '420px !important']}
      minWidth={['', '', '420px !important']}
      colorTheme={ModalColorTheme.Darkblue}
    >
      <ModalHeader colorTheme={ModalColorTheme.Darkblue} title="HotKey" />
      <ModalBody>
        <RowBetween mb="16px">
          <Text color="textTertiary" small>
            <Trans>Make a bet</Trans>
          </Text>

          <StyledButtonKey>
            <Text color="text">space</Text>
          </StyledButtonKey>
        </RowBetween>

        <RowBetween mb="16px">
          <Text color="textTertiary" small>
            <Trans>Double bet amount</Trans>
          </Text>

          <StyledButtonKey>
            <Text color="text">s</Text>
          </StyledButtonKey>
        </RowBetween>

        <RowBetween mb="16px">
          <Text color="textTertiary" small>
            <Trans>Halve bet amount</Trans>
          </Text>

          <StyledButtonKey>
            <Text color="text">a</Text>
          </StyledButtonKey>
        </RowBetween>

        <RowBetween mb="16px">
          <Text color="textTertiary" small>
            <Trans>Zero bet amount</Trans>
          </Text>

          <StyledButtonKey>
            <Text color="text">d</Text>
          </StyledButtonKey>
        </RowBetween>

        {game?.additionalData?.inhouseGameType === InhouseGameType.Mine && (
          <>
            <RowBetween mb="16px">
              <Text color="textTertiary" small>
                <Trans>Pick random tile</Trans>
              </Text>

              <StyledButtonKey>
                <Text color="text">q</Text>
              </StyledButtonKey>
            </RowBetween>

            <RowBetween mb="16px">
              <Text color="textTertiary" small>
                <Trans>Cashout</Trans>
              </Text>

              <StyledButtonKey>
                <Text color="text">w</Text>
              </StyledButtonKey>
            </RowBetween>
          </>
        )}
        <StyledBoxWarning>
          <Flex>
            <Box minWidth="24px">
              <Icons.InfoIcon fill={theme.colors.textSubtle} />
            </Box>
            <Text ml="8px" color="textSubtle" small lineHeight={1.5}>
              <Trans>
                When the hotkeys are enabled, they will remain on for all games until disabled. Despite some games
                sharing similar key binds, it's always advised to confirm what key interactions are set for each game.
              </Trans>
            </Text>
          </Flex>
        </StyledBoxWarning>
      </ModalBody>

      <ModalFooter>
        <RowMiddle mx="auto">
          <Checkbox
            width="24px"
            height="24px"
            value={checked}
            onValueChanged={(value: boolean) => {
              setChecked(value)
              refChecked.current = value
            }}
          />
          <Text ml="8px" color="textTertiary" small>
            <Trans>Hotkeys Enabled</Trans>
          </Text>
        </RowMiddle>
      </ModalFooter>
    </AlchemyModal>
  )
}

const StyledButtonKey = styled(Box)`
  font-size: 24px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt9};

  border-radius: ${({ theme }) => theme.radii.tiny};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 1px 2px 0 rgba(0, 0, 0, 0.12), inset 0 1px rgba(255, 255, 255, 0.04);
`

const StyledBoxWarning = styled(Box)`
  display: flex;
  background: ${({ theme }) => theme.colors.backgroundAlt9};

  padding: 12px;
  border: 2px dashed ${({ theme }) => theme.colors.textSubtle};
  border-radius: ${({ theme }) => theme.radii.small};
  overflow: hidden;
`

export default HotKeyModal
